import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import Tag from '../shared/Tag';

const PAGE_SIZE = 50;

export default function AuditPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [entityFilter, setEntityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [entityTypes, setEntityTypes] = useState([]);

  const load = () => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (entityFilter) params.entity_type = entityFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    api.get('audit', params).then(data => {
      setEntries(data.results || []);
      setTotal(data.total || 0);
      const types = [...new Set((data.results || []).map(e => e.entity_type))].sort();
      if (types.length > entityTypes.length) setEntityTypes(types);
    });
  };
  useEffect(load, [page, entityFilter, dateFrom, dateTo]);

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div><h2 className="text-base font-extrabold text-navy">Audit Trail</h2><div className="text-[9px] text-gray-500">{total} entries &middot; page {page + 1}/{pages || 1}</div></div>
      </div>
      <div className="flex gap-2 mb-2 flex-wrap">
        <select className="form-select max-w-[140px]" value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(0); }}>
          <option value="">All types</option>
          {entityTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="form-input max-w-[140px]" type="date" placeholder="From" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} />
        <input className="form-input max-w-[140px]" type="date" placeholder="To" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} />
        {(entityFilter || dateFrom || dateTo) && (
          <button onClick={() => { setEntityFilter(''); setDateFrom(''); setDateTo(''); setPage(0); }} className="text-[9px] text-gold hover:underline">Clear filters</button>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full"><thead><tr>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Time</th>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">User</th>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Action</th>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Entity</th>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Change</th>
        </tr></thead>
        <tbody>{entries.map(e => (
          <tr key={e.id} className="hover:bg-gold/5">
            <td className="px-2 py-1 text-[8px] text-gray-400 whitespace-nowrap">{e.timestamp}</td>
            <td className="px-2 py-1 text-[9px] font-semibold">{e.person_name}</td>
            <td className="px-2 py-1"><Tag status={e.action === 'created' ? 'active' : e.action === 'deleted' ? 'cancelled' : 'in_progress'}>{e.action}</Tag></td>
            <td className="px-2 py-1 text-[9px]">{e.entity_type}</td>
            <td className="px-2 py-1 text-[10px] font-medium">{e.entity_name || e.entity_id}</td>
            <td className="px-2 py-1 text-[9px]">{e.field_name ? (
              <span><span className="text-gold font-semibold">{e.field_name}</span>: <span className="line-through text-red-400">{e.old_value}</span> → <span className="text-emerald-600 font-semibold">{e.new_value}</span></span>
            ) : '-'}</td>
          </tr>
        ))}{entries.length === 0 && <tr><td colSpan="6" className="text-center py-6 text-[10px] text-gray-400">No audit entries found</td></tr>}</tbody></table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 py-3">
          <button className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white disabled:opacity-30" disabled={page === 0} onClick={() => setPage(0)}>First</button>
          <button className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white disabled:opacity-30" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span className="text-[9px] text-gray-500">{page + 1} / {pages}</span>
          <button className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white disabled:opacity-30" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
          <button className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white disabled:opacity-30" disabled={page >= pages - 1} onClick={() => setPage(pages - 1)}>Last</button>
        </div>
      )}
    </div>
  );
}
